"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sprout, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart-store";

const navLinks = [
  { href: "#accueil", label: "Accueil" },
  { href: "#histoire", label: "Histoire" },
  { href: "#produits", label: "Produits" },
  { href: "#transformation", label: "Transformation" },
  { href: "#infrastructure", label: "Infrastructure" },
  { href: "#gammes", label: "Gammes" },
  { href: "#degustation", label: "Dégustation" },
  { href: "#boutique", label: "Boutique" },
  { href: "#logistique", label: "Logistique" },
  { href: "#ecole", label: "École" },
  { href: "#export", label: "Export" },
  { href: "#contact", label: "Contact" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { setOpen: setCartOpen, totalItems } = useCart();
  const cartCount = totalItems();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-gold shadow-sm py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => handleNav("#accueil")}
          className="flex items-center gap-3 group"
          aria-label="PATATE OR — Accueil"
        >
          <span className="relative grid place-items-center w-11 h-11 rounded-full bg-gradient-gold shadow-gold">
            <Sprout className="w-5 h-5 text-white" strokeWidth={2.2} />
            <span className="absolute inset-0 rounded-full ring-1 ring-white/30" />
          </span>
          <span className="flex flex-col items-start leading-none">
            <span className="font-display text-xl font-extrabold tracking-wide text-gradient-gold">
              PATATE OR
            </span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mt-1 hidden sm:block">
              Racines · Tubercules · Or
            </span>
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNav(l.href)}
              className="relative px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors group"
            >
              {l.label}
              <span className="pointer-events-none absolute left-1/2 -bottom-0.5 h-px w-0 bg-gradient-gold transition-all duration-300 group-hover:w-2/3 group-hover:left-[16.66%]" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Cart button */}
          <button
            onClick={() => setCartOpen(true)}
            className="relative grid place-items-center w-10 h-10 rounded-full border border-gold text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Ouvrir le panier"
          >
            <ShoppingCart className="w-4 h-4" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 grid place-items-center min-w-5 h-5 px-1 rounded-full bg-gradient-gold text-white text-[10px] font-bold shadow-gold">
                {cartCount}
              </span>
            )}
          </button>

          <Button
            onClick={() => handleNav("#boutique")}
            className="hidden sm:inline-flex bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg hover:scale-[1.03] transition-all font-semibold rounded-full"
          >
            Boutique
          </Button>

          {/* Mobile toggle */}
          <button
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-gold text-foreground"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-t border-gold"
          >
            <div className="container mx-auto max-w-7xl px-4 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l.href)}
                  className="text-left px-4 py-3 rounded-lg text-foreground/85 hover:bg-secondary/70 hover:text-foreground transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <Button
                onClick={() => handleNav("#contact")}
                className="mt-2 bg-gradient-gold text-white shadow-gold rounded-full"
              >
                Commander maintenant
              </Button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
